import { useState, useRef } from 'react'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { Typography, Button, Checkbox, Input } from '@evocolabs/orkaui'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import Header from '@/src/components/HeaderV2'

import { validateEmail } from '@/src/utils/Validater'
import { useBackend } from '@/src/providers/BackendProvider'

import styles from '@/src/components/scss/LegalData.module.scss'

const NonExist = ({ email }) => {
  const submit = () => {
    window.location.href = '/legal/DataDeletion'
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Error</Typography>
      <Typography variant="body3">
        We cannot find any survey data associated with this email address (
        {email}) as a guest user. If you have created an Orka account with this
        email address, please use the account deletion request form.
        <br />
        Note: you may need to login to your Orka account to access the form.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          loadable
          id="survey-data-deletion-page-request-button"
        >
          Request Account Deletion
        </Button>
      </div>
    </div>
  )
}

const View1 = ({ setView, setUserEmail }) => {
  const [error, setError] = useState(null)
  const { requestSurveyDataDelete } = useBackend()
  const submit = async () => {
    const email = document.getElementById('input_confirmation').value
    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
    } else {
      // TODO: call backend api
      setUserEmail(email)
      setError(null)
      const res = await requestSurveyDataDelete(email)
      if (!res) {
        setView('nonExist')
      } else {
        setView((d) => d + 1)
      }
    }
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Survey Data Deletion Request</Typography>
      <Typography variant="body3">
        Please enter the email address you used for the survey, so we can
        properly identify you.
      </Typography>
      <Input
        placeholder="Your Email Address"
        fullWidth
        type="email"
        id="input_confirmation"
        errormsg={error}
      />
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          loadable
          id="survey-data-deletion-page-submit-email-button"
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

const View2 = ({ userEmail }) => {
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Survey Data Deletion Request</Typography>
      <Typography variant="body3">
        We will send a data deletion request link to your email ({userEmail}),
        please check.
      </Typography>
    </div>
  )
}

const View3 = ({ setView }) => {
  const [error, setError] = useState(null)
  const router = useRouter()
  const token = router.query.token
  const { confirmSurveyDataDelete } = useBackend()
  const uuid = jwtDecode(token).user_id
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Survey Data Deletion Request</Typography>
      <Typography variant="body3">
        Please note that Orka may not be able to recover your survey
        information.
      </Typography>
      <Checkbox
        label="I agree to the above, please proceed."
        id="confirm_checkbox"
        errormsg={error}
      />
      <Button
        variant="primary"
        size="large"
        loadable
        handleClick={async () => {
          const checkbox = document.getElementById('confirm_checkbox')
          if (checkbox.checked) {
            await confirmSurveyDataDelete(uuid, token)
            setView((d) => d + 1)
          } else {
            setError('Please confirm your request.')
          }
        }}
        id="survey-data-deletion-page-confirm-deletion-button"
      >
        Delete Data
      </Button>
    </div>
  )
}

const View4 = () => {
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Successfully Submitted</Typography>
      <Typography variant="body3">
        Your survey data will be deleted in within 30 days, and we will notify
        you of the results as soon as deletion is complete. If you have any
        questions, please contact us.
      </Typography>

      <Button
        variant="primary"
        size="large"
        handleClick={() => {
          window.location.href = '/'
        }}
        id="survey-data-deletion-page-understand-button"
      >
        Understand
      </Button>
    </div>
  )
}

const SurveyDataDeletion = () => {
  const router = useRouter()
  const [view, setView] = useState(1)
  const [userEmail, setUserEmail] = useState(null)
  const token = router.query.token
  const validToken = useRef(true)
  if (token) {
    const decoded = jwtDecode(token)
    // Check if token is expired
    const tokenDate = new Date(decoded.exp * 1000)
    const dateDiff = moment().diff(tokenDate, 'seconds')
    if (dateDiff > 0 && view !== 'expired') {
      setView('expired')
      validToken.current = false
    }
    if (view < 3 && validToken.current) {
      setView(3)
    }
  } else {
    if (view === -1) {
      setView(1)
    }
  }
  return (
    <>
      <style>{`html, body, #vh-relative { background-color: #F7F3EB !important;}`}</style>
      <NextSeo
        title="Delete Survey Data | Orka - Hear what matters"
        description="Delete Personal Data"
      />
      <Header iswhitebg />
      {view === 1 && <View1 setView={setView} setUserEmail={setUserEmail} />}
      {view === 2 && <View2 userEmail={userEmail} />}
      {view === 3 && <View3 setView={setView} />}
      {view === 4 && <View4 />}
      {view === 'nonExist' && <NonExist email={userEmail} />}
    </>
  )
}

export default SurveyDataDeletion
